import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * A reusable confirmation modal component
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Function to call when closing the modal
 * @param {Function} props.onConfirm - Function to call when confirming the action
 * @param {string} props.title - Modal title
 * @param {string} props.message - Modal message
 * @param {string} props.confirmText - Text for the confirm button
 * @param {string} props.cancelText - Text for the cancel button
 * @param {string} props.confirmButtonClass - Additional CSS class for confirm button
 */
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  confirmButtonClass = 'bg-red-600 hover:bg-red-700'
}) => {
  const { t } = useTranslation();
  
  if (!isOpen) return null;
  
  // Default texts in case they're not provided
  const defaultTitle = t('modal.confirmAction', 'Confirm Action');
  const defaultMessage = t('modal.areYouSure', 'Are you sure you want to proceed with this action?');
  const defaultConfirmText = t('modal.confirm', 'Confirm');
  const defaultCancelText = t('modal.cancel', 'Cancel');

  // Handle clicking outside the modal to close it
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-lg shadow-lg max-w-md w-full mx-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-2">
            {title || defaultTitle}
          </h3>
          <p className="text-gray-600 mb-6">
            {message || defaultMessage}
          </p>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              {cancelText || defaultCancelText}
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className={`px-4 py-2 rounded-md text-white ${confirmButtonClass}`}
            >
              {confirmText || defaultConfirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal; 